/* cf-two-gates.js - Click Fencing, frameless glass calculator - 3 Oct 2026
 *
 * TWO GATES IN ONE SECTION, AND A GATE HINGED TO A STAINLESS STEEL POST.
 *
 * Pete, 3 Oct 2026: "there is a problem with the calculator, it wont do 2 gates in 1 section".
 * Found on Augustine Chin's CF-1012: Run 1 = 9500mm, 1200 high - wall | 10 | 900 gate (hinges to
 * the wall) | 7 | five 1500 panels, 29mm gaps | 7 | 900 gate | 10 | 50x50 stainless post. It had
 * to be laid out by hand. Pete's rule the same day: a gate that needs something to hinge to hangs
 * off a James Glen stainless steel post (48458, 50x50x1800 G316 Mirror).
 *
 * WHAT IT ADDS, on the Gate panel of any glass section:
 *   - "Gate hinges to": Wall / Glass hinge panel (as the calculator already decided) or a
 *     Stainless steel post, for gate 1 and gate 2.
 *   - "Second gate in this section?": gate 2's length, hinge side, position and what it hinges to.
 *   - Each gate's position: at the left end, at the right end, or measured from an end.
 * Turning these on switches the section to its own solver (below). Every section without them
 * runs the calculator's own code, untouched.
 *
 * HOW THE LAYOUT IS SOLVED. The calculator constants: 10mm hinge gap, 7mm latch gap, variable gaps
 * 10-80mm aiming at ~25mm, pool panels on the 50mm ladder (100-2000 + 2100, never above the max
 * panel chosen), glass hinge panels on the 100mm ladder (600-1800). Gates at an end sit against
 * it; a measured gate moves the least it has to (1mm steps) so every run is whole panels, and the
 * customer is told by how much. A section too short for the gates is snapped up to the shortest
 * length that builds, with a note (Pete, 30 Aug 2026, the single-gate rule).
 *
 * WHAT IT CHANGES DOWNSTREAM, all from the one solved layout so drawing, quote and cart agree:
 *   drawing   - both gates (each with its own hinge side, a plan-view swing mark under it: box
 *               between posts, 90deg hinge line, dotted arc - Pete 28 Sep), the post drawn as a post
 *   quote     - gate 2's gate panel, hinge pair and latch from the same products as gate 1; the
 *               post at its store price; S&I adds $180 a gate and $300 a post (Pete 3 Oct)
 *   cart      - every line carries its variant, so cf-real-line-items.js orders the real parts
 *   quote page / PDF / CRM - the payload carries each gate's hinge side and the post
 *
 * THE POST is sold only once its Shopify product (handle below) is live; until then the option
 * is hidden. localStorage.cf_post_test = '1' shows it for testing, priced as on the CF-1012 draft.
 *
 * Self-installing, idempotent, every hook in try/catch, glass page only.
 */
/* cf-two-gates solver - pure layout maths, no DOM. Shared by the theme asset (pasted in) and the
 * Node tests (required). Physical order is LEFT -> RIGHT along the run.
 *
 *   gate block, hinge LEFT :  [post?] 10 [gate] 7      (hinge to wall / post / glass hinge panel)
 *   gate block, hinge RIGHT:  7 [gate] 10 [post?]
 *
 * Between and beside the gate blocks are RUNS of glass: regular panels (50mm ladder, 100-2000 +
 * 2100, never above the customer's max panel), a glass hinge panel (100mm ladder 600-1800) where a
 * gate hinges to glass, and the raked panel at its end. Variable gaps 10-80mm. */
var CF2G = (function () {
  'use strict';
  var HINGE = 10, LATCH = 7, MINGAP = 10, MAXGAP = 80, POST_W = 50;
  var LADDER = [];
  for (var w = 100; w <= 2000; w += 50) LADDER.push(w);
  LADDER.push(2100);
  var HLADDER = [];
  for (var h = 600; h <= 1800; h += 100) HLADDER.push(h);

  function hingeAtEnd(g) { return (g.place === 'left' && g.side === 'left') || (g.place === 'right' && g.side === 'right'); }
  function latchAtEnd(g) { return (g.place === 'left' && g.side === 'right') || (g.place === 'right' && g.side === 'left'); }

  /* A gate's mount can only be what its position allows: a WALL only where the hinge meets a
     section end, a glass HINGE PANEL only where it does not. A post works anywhere. */
  function normaliseGate(g) {
    var o = {};
    for (var k in g) o[k] = g[k];
    o.w = parseInt(o.w, 10) || 800;
    o.side = o.side === 'left' ? 'left' : 'right';
    o.place = (o.place === 'left' || o.place === 'right') ? o.place : 'measured';
    o.from = o.from === 'right' ? 'right' : 'left';
    o.mm = Math.max(0, parseInt(o.mm, 10) || 0);
    var atEnd = hingeAtEnd(o);
    if (o.mount !== 'post') o.mount = atEnd ? 'wall' : 'glass';
    return o;
  }

  function startX(g, L) {
    var post = g.mount === 'post' ? POST_W : 0;
    if (g.place === 'left') return hingeAtEnd(g) ? HINGE + post : LATCH;
    if (g.place === 'right') return hingeAtEnd(g) ? L - g.w - HINGE - post : L - g.w - LATCH;
    return g.from === 'left' ? g.mm : L - g.mm - g.w;
  }

  // Block extent and what each side presents to the run next to it.
  function block(g, x0) {
    var post = g.mount === 'post' ? POST_W : 0;
    var hingeKind = g.mount === 'post' ? 'post' : (g.mount === 'wall' ? 'wall' : 'hinge');
    if (g.side === 'left') {
      return { lo: x0 - HINGE - post, hi: x0 + g.w + LATCH, leftKind: hingeKind, rightKind: 'latch' };
    }
    return { lo: x0 - LATCH, hi: x0 + g.w + HINGE + post, leftKind: 'latch', rightKind: hingeKind };
  }

  function blockTokens(g) {
    var gate = { type: 'gate', width: g.w, flipped: g.side === 'right', cfGate: g.num };
    var post = { type: 'post', width: POST_W, cfGate: g.num };
    if (g.side === 'left') {
      return g.mount === 'post' ? [post, HINGE, gate, LATCH] : [HINGE, gate, LATCH];
    }
    return g.mount === 'post' ? [LATCH, gate, HINGE, post] : [LATCH, gate, HINGE];
  }

  function edgeGap(kind, endZero) {
    if (kind === 'end') return endZero ? 0 : 'var';
    if (kind === 'post') return 'var';
    return 0;                                   // latch / hinge / wall: hardware gap lives in the block
  }

  /* Fit one run. Fewest regular panels first (cheapest, and what every other glass solver does);
     within that count the gaps nearest the target win, then the most even panel sizes. */
  function fitRun(run, P, scoreIt) {
    var len = run.len;
    if (len < 0) return null;
    if (len === 0) return run.emptyOK ? { items: [], gl: 0, gr: 0, inner: [], n: 0 } : null;
    if (run.mustBeEmpty) return null;
    var gl = edgeGap(run.leftKind, run.leftEndZero), gr = edgeGap(run.rightKind, run.rightEndZero);
    // A run can need a glass hinge panel at EITHER end - or both, when two gates hinge towards it.
    var hingeL = run.leftKind === 'hinge', hingeR = run.rightKind === 'hinge';
    var nh = (hingeL ? 1 : 0) + (hingeR ? 1 : 0);
    var rakeW = run.rake ? run.rake.w : 0;
    var hList = [];
    (hingeL ? HLADDER : [0]).forEach(function (a) { (hingeR ? HLADDER : [0]).forEach(function (b) { hList.push([a, b]); }); });
    var lad = [];
    for (var i = 0; i < LADDER.length; i++) if (LADDER[i] >= P.minp && LADDER[i] <= P.ceil) lad.push(LADDER[i]);

    if (!scoreIt) {
      // Fast yes/no: n panels on a 50mm grid can make any multiple of 50 between n*min and n*max,
      // so a count works when that band meets the band the gaps allow.
      var maxw = Math.min(P.ceil, 2000);
      for (var fn = 0; fn <= 24; fn++) {
        var fk = fn + (run.rake ? 1 : 0) + nh;
        if (fk === 0) continue;
        var fvg = (fk - 1) + (gl === 'var' ? 1 : 0) + (gr === 'var' ? 1 : 0);
        for (var fh = 0; fh < hList.length; fh++) {
          var frem = len - rakeW - hList[fh][0] - hList[fh][1];
          if (frem < 0) continue;
          if (fn === 0) {
            if (fvg === 0 ? frem === 0 : (frem >= fvg * MINGAP && frem <= fvg * MAXGAP)) return { feasible: true };
            continue;
          }
          var lo = Math.max(fn * P.minp, frem - fvg * MAXGAP), hi2 = Math.min(fn * maxw, frem - fvg * MINGAP);
          if (fvg === 0) { if (frem % 50 === 0 && frem >= fn * P.minp && frem <= fn * maxw) return { feasible: true }; continue; }
          if (hi2 < lo) continue;
          if (Math.floor(hi2 / 50) * 50 >= lo) return { feasible: true };
        }
        if (fn * P.minp > len) break;
      }
      return null;
    }

    for (var n = 0; n <= 24; n++) {
      var k = n + (run.rake ? 1 : 0) + nh;
      if (k === 0) continue;
      var vg = (k - 1) + (gl === 'var' ? 1 : 0) + (gr === 'var' ? 1 : 0);
      var best = null;
      for (var hi = 0; hi < hList.length; hi++) {
        var HL = hList[hi][0], HR = hList[hi][1];
        var rem = len - rakeW - HL - HR;
        if (rem < 0) continue;
        var cands = [];
        if (n === 0) cands.push({ big: 0, pw: 0, a: 0 });
        else {
          for (var li = 0; li < lad.length; li++) {
            var pw = lad[li], big = lad[li + 1] || 0;
            if (n * pw > rem) break;
            for (var a = 0; a <= n; a++) {
              if (a > 0 && !big) break;
              cands.push({ big: big, pw: pw, a: a });
            }
          }
        }
        for (var ci = 0; ci < cands.length; ci++) {
          var c = cands[ci];
          var used = c.a * c.big + (n - c.a) * c.pw;
          var pool = rem - used;
          if (vg === 0) { if (pool !== 0) continue; }
          else {
            var base = Math.floor(pool / vg), extra = pool - base * vg;
            if (base < MINGAP || base + (extra > 0 ? 1 : 0) > MAXGAP) continue;
          }
          if (!scoreIt) return { feasible: true };
          var gaps = [];
          if (vg > 0) { for (var z = 0; z < vg; z++) gaps.push(base + (z < extra ? 1 : 0)); }
          var dev = 0;
          for (z = 0; z < gaps.length; z++) dev += Math.abs(gaps[z] - P.tgap);
          dev = gaps.length ? dev / gaps.length : 0;
          if (c.a > 0 && c.a < n) dev += 0.01;
          var widths = [];
          for (z = 0; z < c.a; z++) widths.push(c.big);
          for (z = 0; z < n - c.a; z++) widths.push(c.pw);
          var glass = widths.concat(HL ? [HL] : [], HR ? [HR] : []);
          var spread = glass.length ? Math.max.apply(null, glass) - Math.min.apply(null, glass) : 0;
          var cand = { HL: HL, HR: HR, widths: widths, gaps: gaps, dev: dev, spread: spread };
          if (!best) best = cand;
          else {
            var wins = Math.abs(dev - best.dev) > 1 ? dev < best.dev : (spread !== best.spread ? spread < best.spread : dev < best.dev);
            if (wins) best = cand;
          }
        }
      }
      if (best) {
        var items = [];
        if (run.rake && run.rake.side === 'left') items.push({ type: 'raked', width: rakeW, flipped: false, retainedHeight: run.rake.retainedHeight });
        if (hingeL) items.push({ type: 'hinge-panel', width: best.HL, cfFacing: 'left', cfGate: run.hingeGateL });
        best.widths.forEach(function (w) { items.push({ type: 'panel', width: w }); });
        if (hingeR) items.push({ type: 'hinge-panel', width: best.HR, cfFacing: 'right', cfGate: run.hingeGateR });
        if (run.rake && run.rake.side === 'right') items.push({ type: 'raked', width: rakeW, flipped: true, retainedHeight: run.rake.retainedHeight });
        var gi = 0;
        var outL = gl === 'var' ? best.gaps[gi++] : 0;
        var inner = [];
        for (var q = 0; q < items.length - 1; q++) inner.push(best.gaps[gi++]);
        var outR = gr === 'var' ? best.gaps[gi++] : 0;
        return { items: items, gl: outL, gr: outR, inner: inner, n: n, dev: best.dev };
      }
    }
    return null;
  }

  function runsFor(gs, xs, cfg) {
    // gs sorted by x; xs = their start positions
    var L = cfg.L, mode = cfg.endMode || 'gap-both';
    var zl = mode === 'no-gap-left' || mode === 'no-gap-both';
    var zr = mode === 'no-gap-right' || mode === 'no-gap-both';
    var bs = gs.map(function (g, i) { return block(g, xs[i]); });
    var runs = [];
    for (var i = 0; i <= gs.length; i++) {
      var lo = i === 0 ? 0 : bs[i - 1].hi;
      var hi = i === gs.length ? L : bs[i].lo;
      var leftKind = i === 0 ? 'end' : bs[i - 1].rightKind;
      var rightKind = i === gs.length ? 'end' : bs[i].leftKind;
      var r = { len: hi - lo, leftKind: leftKind, rightKind: rightKind, leftEndZero: zl, rightEndZero: zr };
      if (leftKind === 'hinge') r.hingeGateL = gs[i - 1].num;
      if (rightKind === 'hinge') r.hingeGateR = gs[i].num;
      var isEnd = i === 0 || i === gs.length;
      var facing = i === 0 ? rightKind : leftKind;
      if (cfg.rake && ((i === 0 && cfg.rake.side === 'left') || (i === gs.length && cfg.rake.side === 'right'))) r.rake = cfg.rake;
      r.emptyOK = isEnd && !r.rake && (facing === 'wall' || facing === 'post' || facing === 'latch');
      r.mustBeEmpty = isEnd && facing === 'wall';
      if (!isEnd && (leftKind === 'wall' || rightKind === 'wall')) r.bad = true;
      runs.push(r);
    }
    return runs;
  }

  function feasibleAt(gs, xs, cfg, P, memo) {
    for (var i = 1; i < gs.length; i++) if (xs[i] < xs[i - 1]) return false;
    var runs = runsFor(gs, xs, cfg);
    for (var r = 0; r < runs.length; r++) {
      var run = runs[r];
      if (run.bad) return false;
      var key = r + ':' + run.leftKind + ':' + run.rightKind + ':' + (run.rake ? 1 : 0) + ':' + run.len;
      var ok = memo[key];
      if (ok === undefined) ok = memo[key] = !!fitRun(run, P, false);
      if (!ok) return false;
    }
    return true;
  }

  function assemble(gs, xs, cfg, P) {
    var runs = runsFor(gs, xs, cfg);
    var fits = runs.map(function (r) { return fitRun(r, P, true); });
    if (fits.some(function (f) { return !f; })) return null;
    var tokens = [];
    for (var i = 0; i < runs.length; i++) {
      var f = fits[i];
      tokens.push(f.gl);
      f.items.forEach(function (it, k) { tokens.push(it); if (k < f.items.length - 1) tokens.push(f.inner[k]); });
      tokens.push(f.gr);
      if (i < gs.length) blockTokens(gs[i]).forEach(function (t) { tokens.push(t); });
    }
    var items = [], gaps = [], acc = 0;
    tokens.forEach(function (t) {
      if (typeof t === 'number') { acc += t; return; }
      gaps.push(acc); acc = 0; items.push(t);
    });
    gaps.push(acc);
    // Which gates really sit against an end (an empty end run) - that decides wall latch / post at the end.
    var info = gs.map(function (g, i) {
      var leftRun = runs[i], rightRun = runs[i + 1];
      var hingeSideRun = g.side === 'left' ? leftRun : rightRun;
      var latchSideRun = g.side === 'left' ? rightRun : leftRun;
      var latchEnd = (latchSideRun.len === 0) && (latchSideRun.leftKind === 'end' || latchSideRun.rightKind === 'end');
      var hingeEnd = (hingeSideRun.len === 0) && (hingeSideRun.leftKind === 'end' || hingeSideRun.rightKind === 'end');
      return { num: g.num, w: g.w, side: g.side, mount: g.mount, x0: xs[i], latchAtEnd: latchEnd, hingeAtEnd: hingeEnd,
               latchEnd: g.latchEnd, place: g.place, wantX0: g._want, shift: xs[i] - g._want };
    });
    var total = gaps.reduce(function (a, b) { return a + b; }, 0) + items.reduce(function (a, t) { return a + t.width; }, 0);
    if (total !== cfg.L) return null;
    return { items: items, gaps: gaps, gates: info };
  }

  /* Solve. Gates placed at an end stay there; a MEASURED gate moves the least it has to (1mm steps,
     up to 1500mm) so that every run fills with whole panels on the ladder. */
  function solve(cfg) {
    var gates = cfg.gates.map(normaliseGate);
    var L = parseInt(cfg.L, 10) || 0;
    cfg.L = L;
    if (!L || !gates.length) return { error: 'empty' };
    var xs0 = gates.map(function (g) { return startX(g, L); });
    gates.forEach(function (g, i) { g._want = xs0[i]; });
    var order = gates.map(function (g, i) { return i; }).sort(function (a, b) { return xs0[a] - xs0[b]; });
    var gs = order.map(function (i) { return gates[i]; });
    var base = order.map(function (i) { return xs0[i]; });
    var movable = gs.map(function (g) { return g.place === 'measured'; });
    if (cfg.rake) {
      for (var i = 0; i < gs.length; i++) {
        if (gs[i].place === cfg.rake.side) return { error: 'rake-end', gate: gs[i].num };
      }
    }
    var tries = [200, 100];
    for (var t = 0; t < tries.length; t++) {
      var P = { minp: tries[t], ceil: cfg.ceil || 1800, tgap: cfg.tgap || 25 };
      var MAXS = 1500;
      var found = null;
      // The run KINDS never change with a shift, only their lengths - so test each run per length once.
      var tmpl = runsFor(gs, base, cfg);
      if (tmpl.some(function (r) { return r.bad; })) return { error: 'no-fit' };
      var memo = tmpl.map(function () { return {}; });
      var okLen = function (ri, len) {
        var m = memo[ri];
        if (m[len] === undefined) {
          var r = {}; for (var kk in tmpl[ri]) r[kk] = tmpl[ri][kk];
          r.len = len;
          m[len] = !!fitRun(r, P, false);
        }
        return m[len];
      };
      if (gs.length === 1) {
        for (var s = 0; s <= (movable[0] ? MAXS : 0) && !found; s++) {
          var cands = s === 0 ? [0] : [s, -s];
          for (var c = 0; c < cands.length; c++) {
            var d = cands[c];
            if (okLen(0, tmpl[0].len + d) && okLen(1, tmpl[1].len - d)) { found = [base[0] + d]; break; }
          }
        }
      } else {
        var maxA = movable[0] ? MAXS : 0, maxB = movable[1] ? MAXS : 0;
        // Shifts each OUTER run can take, nearest first; give up at once if either has none.
        var byAbs = function (max, ok) {
          var out = [];
          for (var d0 = 0; d0 <= max; d0++) { if (ok(d0)) out.push(d0); if (d0 && ok(-d0)) out.push(-d0); }
          return out;
        };
        var FA = byAbs(maxA, function (d) { return okLen(0, tmpl[0].len + d); });
        var FB = byAbs(maxB, function (d) { return okLen(2, tmpl[2].len - d); });
        var bestTot = Infinity;
        for (var ia = 0; ia < FA.length; ia++) {
          var da = FA[ia];
          if (Math.abs(da) >= bestTot) break;
          for (var ib = 0; ib < FB.length; ib++) {
            var db = FB[ib];
            if (Math.abs(da) + Math.abs(db) >= bestTot) break;
            var mid = tmpl[1].len + db - da;
            if (mid < 0) continue;
            if (okLen(1, mid)) { bestTot = Math.abs(da) + Math.abs(db); found = [base[0] + da, base[1] + db]; break; }
          }
          if (ia > 4000) break;
        }
      }
      if (found) {
        var out = assemble(gs, found, cfg, P);
        if (out) return out;
      }
    }
    return { error: 'no-fit' };
  }

  /* The shortest section length (from L upward) that builds this gate set - used to snap a section
     that is too short, the same way the single-gate fit does (Pete, 30 Aug 2026). */
  function minLength(cfg, upTo) {
    for (var L = cfg.L; L <= (upTo || cfg.L + 4000); L += 1) {
      var c = {}; for (var k in cfg) c[k] = cfg[k];
      c.L = L;
      var r = solve(c);
      if (!r.error) return L;
    }
    return null;
  }

  return { solve: solve, minLength: minLength, normaliseGate: normaliseGate, hingeAtEnd: hingeAtEnd,
           latchAtEnd: latchAtEnd, POST_W: POST_W, HINGE: HINGE, LATCH: LATCH };
})();

/* ---------------------------------------------------------------------------------------------
 * Calculator integration. Everything below wraps GlassFencingCalculator.prototype directly, as
 * soon as this deferred script runs - BEFORE the instance exists and before any other patch
 * installs - so every other patch (EnduroShield coating, hinge-panel pricing, SKU stamping, GST)
 * wraps OURS and treats the second gate's lines exactly like the first gate's.
 *
 * A section is in "custom gates" mode when it has a gate AND (a second gate, OR the customer has
 * chosen what the first gate hinges to - a stainless post, or back to a wall / hinge panel after
 * that). Only then does any of this change behaviour; every other section runs the calculator's
 * own code untouched (proven: 54 ordinary configurations hash identically to live, 3 Oct 2026).
 * ------------------------------------------------------------------------------------------- */
(function () {
  'use strict';
  var TAG = '[cf-two-gates]';
  if (window.__cfTwoGatesInstalled) return;

  function onGlassPage() {
    return !!(document.getElementById('spigot-shape') || document.getElementById('hw-sel-latch'));
  }
  if (typeof GlassFencingCalculator === 'undefined' || !onGlassPage()) return;
  var P = GlassFencingCalculator.prototype;
  if (P.__cfTwoGatesPatched) return;
  window.__cfTwoGatesInstalled = true;

  var DEF_RETAINED = (typeof CONFIG !== 'undefined' && CONFIG.DEFAULTS && CONFIG.DEFAULTS.RETAINED_HEIGHT) || 1400;
  var POST_PRODUCT_HANDLE = 'stainless-steel-gate-post-50x50x1800';
  var POST_SKU = { mirror: '48458', black: '48459BK' };
  /* Base-plate post (Pete, 4 Oct 2026: "they can choose inground for the stainless post or basemount"):
     JG 50624 Mirror / 50626 Black / 50627 White, 50.8 x 1300 with base, cap & cover. */
  var BASE_POST_HANDLE = 'stainless-steel-gate-post-base-plate-50x50x1300';
  var BASE_POST_SKU = { mirror: '50624', black: '50626', white: '50627' };
  var BASE_POST_TEST_CENTS = { mirror: 13986, black: 14457, white: 14457 };
  var basePost = { byColour: {} };
  /* Test-only fallback prices (cents) for a browser with localStorage.cf_post_test = '1'. They are
     what the CF-1012 draft used (JG cost x the glass spigot markup) - never shown to a customer:
     without the real product the post option is hidden. */
  var POST_TEST_CENTS = { mirror: 15557, black: 16516 };
  var post = { loaded: false, byColour: {} };

  function warn(m, e) { try { console.warn(TAG + ' ' + m, e && (e.message || e)); } catch (x) {} }
  function testMode() { try { return localStorage.getItem('cf_post_test') === '1'; } catch (e) { return false; } }

  function loadPostProduct() {
    try {
      fetch('/products/' + BASE_POST_HANDLE + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants) return;
          p.variants.forEach(function (v) {
            var sku = String(v.sku || '');
            for (var k in BASE_POST_SKU) if (BASE_POST_SKU[k] === sku && v.available !== false) basePost.byColour[k] = { id: v.id, cents: v.price, sku: sku };
          });
          try { refreshAllUi(); } catch (e) {}
        }).catch(function () {});
    } catch (e) {}
    try {
      fetch('/products/' + POST_PRODUCT_HANDLE + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          post.loaded = true;
          if (!p || !p.variants) return;
          p.variants.forEach(function (v) {
            var sku = String(v.sku || '');
            for (var k in POST_SKU) if (POST_SKU[k] === sku && v.available !== false) post.byColour[k] = { id: v.id, cents: v.price, sku: sku };
          });
          try { refreshAllUi(); } catch (e) {}
        })
        .catch(function () { post.loaded = true; });
    } catch (e) { post.loaded = true; }
  }

  function postFor(colourKey, fix) {
    if (fix === 'base') {
      var kb = colourKey === 'black' || colourKey === 'white' ? colourKey : 'mirror';
      if (basePost.byColour[kb]) return basePost.byColour[kb];
      if (testMode()) return { id: null, cents: BASE_POST_TEST_CENTS[kb], sku: BASE_POST_SKU[kb], test: true };
      return null;
    }
    var k = colourKey === 'black' ? 'black' : (colourKey === 'mirror' ? 'mirror' : null);
    if (!k) return null;
    if (post.byColour[k]) return post.byColour[k];
    if (testMode()) return { id: null, cents: POST_TEST_CENTS[k], sku: POST_SKU[k], test: true };
    return null;
  }

  // Custom when there is a second gate, or the customer has CHOSEN what the gate hinges to (only a
  // change away from the calculator's own choice can set it - see the g1-mount handler).
  function isCustom(s) { return !!(s && s.hasGate && (s.hasGate2 || s.cfG1Mount === 'post' || s.cfG1Mount === 'wall' || s.cfG1Mount === 'glass')); }
  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function num(v) { var n = parseInt(v, 10); return isFinite(n) ? n : 0; }
  // How the stainless post is fixed: the customer's choice, else the section's spigot fixing.
  function postFix(gc, s) {
    if (s && (s.cfPostFix === 'base' || s.cfPostFix === 'inground')) return s.cfPostFix;
    var t = ''; try { t = String(gc.resolveSectionFinish(s).spigotType || ''); } catch (e) {}
    return /base/i.test(t) ? 'base' : 'inground';
  }
  function anyPost(gc, ck) { return !!(postFor(ck, 'inground') || postFor(ck, 'base')); }
  function colourKey(gc, s) { try { return gc.resolveSectionFinish(s).hingeLatchColour || 'mirror'; } catch (e) { return 'mirror'; } }

  function cfgFor(gc, s) {
    var g1w = num(s.gateLength) || 800;
    var gates = [{ num: 1, w: g1w, side: s.gateSide === 'left' ? 'left' : 'right', place: s.cfG1Place || 'left',
                   from: s.cfG1From || 'left', mm: num(s.cfG1Mm), mount: s.cfG1Mount === 'post' ? 'post' : 'auto',
                   latchEnd: s.cfG1LatchEnd || '' }];
    if (s.hasGate2) {
      gates.push({ num: 2, w: num(s.cfG2Length) || g1w, side: s.cfG2Side === 'left' ? 'left' : 'right',
                   place: s.cfG2Place || 'right', from: s.cfG2From || 'right', mm: num(s.cfG2Mm),
                   mount: s.cfG2Mount === 'post' ? 'post' : 'auto', latchEnd: s.cfG2LatchEnd || '' });
    }
    var rake = null;
    if (s.hasRaked) {
      var rw = 1200;
      try { rw = gc.getRakedPanelWidth ? (gc.getRakedPanelWidth() || 1200) : 1200; } catch (e) {}
      rake = { w: rw, side: s.rakedSide === 'right' ? 'right' : 'left', retainedHeight: s.retainedHeight || DEF_RETAINED };
    }
    // ~25mm gaps like the rest of the fence (Pete, 9 Aug 2026), unless the customer has chosen to
    // drive the gap themselves.
    var tgap = s.solvePreference === 'gap' ? (num(s.gapSpacing) || 25) : 25;
    return { L: num(s.length), endMode: s.endGapMode || 'gap-both', rake: rake,
             ceil: Math.min(2100, num(s.panelLength) || 1800), tgap: tgap, gates: gates };
  }

  function buildCalc(gc, s) {
    var cfg = cfgFor(gc, s);
    var r = CF2G.solve(cfg);
    var L = cfg.L;
    var mirror = {
      gateSide: s.gateSide || 'right', gateHingeType: s.gateHingeType || 'glass-to-glass',
      wallGateEnd: s.wallGateEnd || null, rakedSide: s.rakedSide || 'left', endGapMode: s.endGapMode || 'gap-both'
    };
    if (r.error) {
      var bad = { gateExact: true, cfGates: true, cfNoFit: true, cfError: r.error, cfErrorGate: r.gate || null,
                  exactPanels: [], exactStartGap: 0, exactTrailingGaps: [], panelWidth: 0, hingeWidth: 0,
                  numPanels: 0, regularPanels: 0, hingePanels: 0, gatePanels: 1, rakedPanels: s.hasRaked ? 1 : 0,
                  panelWidths: [], gapSpacings: [], fixedGapIndices: [], gapSpacing: 25, totalGaps: 0,
                  originalSectionLength: L, totalRequired: L, gatePosition: s.gatePosition || 0, remaining: 0 };
      for (var k in mirror) bad[k] = mirror[k];
      return bad;
    }
    var items = r.items.map(function (it) {
      var o = {}; for (var k2 in it) o[k2] = it[k2];
      if (o.type !== 'gate') o.flipped = !!o.flipped;
      o.retainedHeight = s.retainedHeight || DEF_RETAINED;
      return o;
    });
    var gaps = r.gaps.slice();
    var regulars = items.filter(function (p) { return p.type === 'panel'; }).map(function (p) { return p.width; });
    var hinges = items.filter(function (p) { return p.type === 'hinge-panel'; });
    var fixedIdx = [];
    gaps.forEach(function (g, i) {
      var a = items[i - 1], b = items[i];
      if ((a && a.type === 'gate') || (b && b.type === 'gate')) fixedIdx.push(i);
    });
    var tally = {};
    gaps.forEach(function (g, i) { if (g > 0 && fixedIdx.indexOf(i) < 0) tally[g] = (tally[g] || 0) + 1; });
    var mode = 25, best = 0;
    for (var gv in tally) if (tally[gv] > best) { best = tally[gv]; mode = parseInt(gv, 10); }
    var calc = {
      gateExact: true, cfGates: true, cfGateInfo: r.gates,
      exactPanels: items, exactStartGap: gaps[0], exactTrailingGaps: gaps.slice(1),
      panelWidth: regulars[0] || (hinges[0] ? hinges[0].width : 0),
      hingeWidth: hinges[0] ? hinges[0].width : 0,
      gateFillWall: false, numVariablePanels: regulars.length, numPanels: regulars.length + hinges.length,
      regularPanels: regulars.length, hingePanels: hinges.length, gateAtRakeEnd: false,
      gatePanels: items.filter(function (p) { return p.type === 'gate'; }).length,
      rakedPanels: s.hasRaked ? 1 : 0,
      gapSpacing: mode, panelWidths: regulars, gapSpacings: gaps.slice(), fixedGapIndices: fixedIdx,
      originalSectionLength: L, totalRequired: L, gatePosition: s.gatePosition || 0,
      totalGaps: gaps.length, totalGapWidth: gaps.reduce(function (a, b) { return a + b; }, 0),
      totalPanelCount: items.length,
      variablePanelWidth: items.reduce(function (a, p) { return a + p.width; }, 0),
      remaining: 0, variance: 0, gapVariance: 0, withinVarianceLimit: true, optimized: false,
      score: 0, panelWeight: 1, gapWeight: 1, remainingScore: 1
    };
    for (var k3 in mirror) calc[k3] = mirror[k3];
    return calc;
  }

  function gatesOf(calc) { return (calc && calc.cfGateInfo) || []; }
  function postsOf(calc) { return ((calc && calc.exactPanels) || []).filter(function (p) { return p.type === 'post'; }).length; }

  /* Which latch the gate needs: glass-to-glass beside a panel; at a section end the customer's
     end choice (default the wall latch). Values are the store's own variant positions. */
  function latchPosition(gc, g, chosen) {
    var positions = [];
    try {
      ((gc.shopifyData.products.gateLatch || {}).variants || []).forEach(function (v) {
        if (v.available !== false && v.position && positions.indexOf(v.position) < 0) positions.push(v.position);
      });
    } catch (e) {}
    var g2g = positions.find(function (p) { return /glass to glass/i.test(p); }) || 'Glass to Glass';
    if (!g.latchAtEnd) return g2g;
    if (chosen && positions.indexOf(chosen) >= 0 && !/glass to glass/i.test(chosen)) return chosen;
    return positions.find(function (p) { return /wall/i.test(p); }) || 'Wall to Glass';
  }

  /* ---------------------------------------------------------------- the solver hooks */

  var oExact = P._gateExactSolve;
  P._gateExactSolve = function (section) {
    try {
      if (isCustom(section)) {
        var c = buildCalc(this, section);
        return c;
      }
    } catch (e) { warn('solve failed', e); }
    return oExact.apply(this, arguments);
  };

  var oUCR = P.updateCalculationResults;
  P.updateCalculationResults = function (sectionNum) {
    var s = this.state && this.state.sections && this.state.sections[sectionNum];
    if (s && isCustom(s)) {
      if (!s._cfInCustom) { s._cfPrevExact = !!s.gateExactMode; s._cfInCustom = true; }
      s.gateExactMode = true;
    } else if (s && s._cfInCustom) {
      s.gateExactMode = !!s._cfPrevExact;
      delete s._cfInCustom; delete s._cfPrevExact;
      if (this.state.optimizedConfigs) delete this.state.optimizedConfigs[sectionNum];
    }
    var out = oUCR.apply(this, arguments);
    if (s && isCustom(s)) { try { afterCalc(this, sectionNum, s); } catch (e) { warn('afterCalc', e); } }
    return out;
  };

  // The movable-small-gate stepper owns its own layout; never let it take a custom section.
  if (typeof P._isSmallGateSection === 'function') {
    var oSmall = P._isSmallGateSection;
    P._isSmallGateSection = function (section) {
      if (isCustom(section)) return false;
      return oSmall.apply(this, arguments);
    };
  }

  // Every consumer that re-derives the layout (Final Preview, the quote page payload, the PDF)
  // gets the solved custom layout itself, never a one-gate reconstruction.
  var oLayout = P.calculatePanelsLayout;
  P.calculatePanelsLayout = function (config) {
    var c = config && config.calculation;
    if (c && c.cfGates) return (c.exactPanels || []).map(function (p) { var o = {}; for (var k in p) o[k] = p[k]; return o; });
    return oLayout.apply(this, arguments);
  };
  var oTrail = P.buildTrailingGaps;
  P.buildTrailingGaps = function (panels, calculation) {
    if (calculation && calculation.cfGates) return (calculation.exactTrailingGaps || []).slice();
    return oTrail.apply(this, arguments);
  };
  var oStart = P.buildStartGap;
  P.buildStartGap = function (calculation, panels) {
    if (calculation && calculation.cfGates) return calculation.exactStartGap || 0;
    return oStart.apply(this, arguments);
  };

  /* ---------------------------------------------------------------- drawing */

  function isCustomPanels(panels) {
    return Array.isArray(panels) && panels.some(function (p) { return p && (p.cfGate || p.type === 'post'); });
  }

  /* The calculator's renderer re-imposes ONE gate's hinge/latch gaps on whatever it is given
     (it would turn the second gate's 10mm hinge gap into 7mm). A custom section is drawn with the
     same scale, elements and gap columns, but with the solved gaps exactly as solved. */
  var oRPP = P.renderPanelPreview;
  P.renderPanelPreview = function (container, panels, gapWidth, startGap, gateSide, gateHingeType, options) {
    if (!container || !isCustomPanels(panels)) return oRPP.apply(this, arguments);
    try {
      var gc = this;
      var gw = Array.isArray(gapWidth) ? gapWidth.slice() : panels.map(function () { return gapWidth; });
      container.innerHTML = '';
      var isMobile = window.innerWidth <= 768;
      var lead = typeof startGap === 'number' ? startGap : (typeof gw[0] === 'number' ? gw[0] : 0);
      var per = panels.map(function (_p, i) { return typeof gw[i] === 'number' ? gw[i] : 0; });
      var RES = isMobile ? 8 : 10;
      var avail = gc._previewAvailablePx(container, (isMobile ? 16 : 44) + RES);
      var gapCount = (lead > 0 ? 1 : 0) + per.filter(function (g) { return g > 0; }).length;
      var pxPerMm = gc._previewFitScale(panels, gapCount, avail);
      var extra = 0;
      panels.forEach(function (p) { if (p.type && p.type.indexOf('rak') > -1) extra = Math.max(extra, (gc._previewRakeTallMm(p) - 1200) * pxPerMm); });
      var padTop = extra > 0 ? (Math.ceil(extra) + 12) : 0;
      var row = document.createElement('div');
      row.className = 'panels-container';
      row.setAttribute('data-cf-two-gates', '1');
      var padBottom = panels.some(function (p) { return p && p.type === 'gate' && p.cfGate; }) ? 38 : 0;   // room for the swing marks
      row.style.cssText = 'display:flex;align-items:flex-end;justify-content:safe center;overflow-x:hidden;overflow-y:visible;' +
        'width:max-content;max-width:100%;margin:0 auto;padding-top:' + padTop + 'px;padding-bottom:' + padBottom + 'px;padding-left:0;padding-right:' + RES + 'px;box-sizing:border-box;';
      row.appendChild(gc.createGapElement(lead, pxPerMm));
      panels.forEach(function (panel, i) {
        var wrap = document.createElement('div');
        wrap.style.cssText = 'display:inline-flex;flex-direction:column;align-items:center;align-self:flex-end;vertical-align:bottom;margin-left:0;margin-right:0;';
        var isRaked = panel.type === 'left-raked' || panel.type === 'right-raked' || panel.type === 'raked';
        var px = (isRaked ? 1200 : (parseInt(panel.width, 10) || 1200)) * pxPerMm;
        wrap.style.width = Math.max(1, px) + 'px';
        wrap.style.overflow = 'visible';
        wrap.appendChild(gc.createPanelElement(panel, px, pxPerMm));
        row.appendChild(wrap);
        row.appendChild(gc.createGapElement(per[i], pxPerMm));
      });
      container.appendChild(row);
      return;
    } catch (e) { warn('draw failed, falling back', e); }
    return oRPP.apply(this, arguments);
  };

  /* A gate in plan: the leaf as a box between its two posts, a solid line from the HINGE post at
     90 degrees (the open leaf), and a dotted arc back to the latch post (Pete's gate symbol,
     28 Sep 2026). Drawn under each gate of a custom section so each one's hinge side reads at a
     glance. */
  function swingGlyph(hingeRight) {
    var W = 44, H = 34, x0 = 4, x1 = W - 4, y = H - 6, r = x1 - x0;
    var hx = hingeRight ? x1 : x0, lx = hingeRight ? x0 : x1;
    var top = y - r * 0.62;   // leaf shown at 90deg, drawn shortened to keep the label row compact
    var sweep = hingeRight ? 0 : 1;
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + ' ' + H + '" aria-hidden="true">' +
      '<rect x="' + (x0 - 3) + '" y="' + (y - 1) + '" width="4" height="6" fill="#475569"/>' +
      '<rect x="' + (x1 - 1) + '" y="' + (y - 1) + '" width="4" height="6" fill="#475569"/>' +
      '<rect x="' + (x0 + 2) + '" y="' + (y + 0.5) + '" width="' + (r - 4) + '" height="3" fill="none" stroke="#c0392b" stroke-width="1"/>' +
      '<line x1="' + hx + '" y1="' + y + '" x2="' + hx + '" y2="' + top + '" stroke="#c0392b" stroke-width="1.6"/>' +
      '<path d="M ' + hx + ' ' + top + ' A ' + r + ' ' + (y - top) + ' 0 0 ' + sweep + ' ' + lx + ' ' + y + '" fill="none" stroke="#c0392b" stroke-width="1" stroke-dasharray="2 2"/>' +
      '</svg>';
    var d = document.createElement('div');
    d.className = 'cf-gate-swing';
    d.style.cssText = 'line-height:0;margin-top:2px;';
    d.style.setProperty('display', 'block', 'important');
    d.innerHTML = svg;
    return d;
  }

  var oCPE = P.createPanelElement;
  P.createPanelElement = function (panel, dynamicWidth, pxPerMm) {
    if (panel && panel.type === 'post') {
      try { return postElement(this, panel, dynamicWidth, pxPerMm); } catch (e) { warn('post draw', e); }
    }
    // A custom section's hinge panels each face their own gate.
    var sec = null, saved;
    if (panel && panel.type === 'hinge-panel' && panel.cfFacing) {
      sec = this.state && this.state.sections && this.state.sections[this.state.currentSection];
      if (sec) { saved = sec.gateSide; sec.gateSide = panel.cfFacing === 'right' ? 'left' : 'right'; }
    }
    var el;
    try { el = oCPE.apply(this, arguments); }
    finally { if (sec) sec.gateSide = saved; }
    try {
      if (panel && panel.type === 'gate' && panel.cfGate && el) {
        var lbl = el.querySelector('.panel-width');
        if (lbl && lbl.firstChild) lbl.lastChild.textContent = 'Gate ' + panel.cfGate;
        // Hung below the label, out of the flow, so the gate glass stays level with the panels.
        var gl = swingGlyph(!!panel.flipped);
        gl.style.position = 'absolute'; gl.style.top = '100%'; gl.style.left = '50%';
        gl.style.transform = 'translateX(-50%)'; gl.style.marginTop = '0';
        el.style.position = 'relative';
        el.appendChild(gl);
        el.setAttribute('data-cf-gate', String(panel.cfGate));
      }
    } catch (e) {}
    return el;
  };

  function postElement(gc, panel, dynamicWidth, pxPerMm) {
    var scale = pxPerMm || (dynamicWidth ? dynamicWidth / 50 : 0.1);
    var cfgSpec = gc._getPreviewSpigotConfig();
    var spec = gc._previewFinishMap()[cfgSpec.finish] || gc._previewFinishMap()['mirror'];
    var wpx = Math.max(7, 50 * scale);
    var div = document.createElement('div');
    div.className = 'panel-preview';
    div.dataset.panelType = 'post';
    div.style.cssText = 'display:inline-flex;flex-direction:column;justify-content:flex-end;position:relative;align-items:center;width:' + wpx + 'px;';
    var glassPx = 1200 * scale, basePx = Math.max(12, 100 * scale);
    var stage = document.createElement('div');
    stage.style.cssText = 'position:relative;flex:0 0 auto;width:' + wpx + 'px;height:' + (glassPx + basePx) + 'px';
    stage.style.setProperty('display', 'block', 'important');
    var bar = document.createElement('div');
    // Post stands from below ground to just above the glass line, capped.
    bar.style.cssText = 'position:absolute;left:0;right:0;bottom:-2px;top:' + Math.max(0, -3) + 'px;background:' + spec.body +
      ';border:1px solid ' + spec.edge + ';border-radius:2px 2px 0 0;box-shadow:inset 2px 0 3px ' + spec.hi + ', inset -2px 0 3px rgba(0,0,0,.28)';
    bar.style.setProperty('display', 'block', 'important');
    stage.appendChild(bar);
    var cap = document.createElement('div');
    cap.style.cssText = 'position:absolute;left:-1px;right:-1px;top:-4px;height:4px;background:' + spec.edge + ';border-radius:1px';
    cap.style.setProperty('display', 'block', 'important');
    stage.appendChild(cap);
    div.appendChild(stage);
    var lbl = document.createElement('div');
    lbl.className = 'panel-width';
    lbl.style.cssText = 'text-align:center;font-size:' + (dynamicWidth && dynamicWidth < 40 ? '9px' : '11px') + ';color:#475569;margin-top:2px;line-height:1.2;min-height:32px;display:flex;flex-direction:column;justify-content:flex-start;width:100%;overflow:visible;';
    ['50mm', 'Post'].forEach(function (t) { var l = document.createElement('div'); l.style.cssText = 'white-space:nowrap;'; l.textContent = t; lbl.appendChild(l); });
    div.appendChild(lbl);
    return div;
  }

  /* ---------------------------------------------------------------- the quote */

  var oQuote = P._calcSectionQuote;
  P._calcSectionQuote = function (sectionNum, section, calculation) {
    if (!(calculation && calculation.cfGates && section && isCustom(section))) return oQuote.apply(this, arguments);
    if (calculation.cfNoFit) return { subtotal: 0, breakdown: [] };
    var gc = this;
    var info = gatesOf(calculation);
    var g1 = info.find(function (g) { return g.num === 1; }) || info[0];
    // Gate 1: the calculator's own lines, told what this layout really needs.
    var s1 = {};
    for (var k in section) s1[k] = section[k];
    s1.gateLength = String(g1.w);
    s1.gateHingeType = g1.mount === 'glass' ? 'glass-to-glass' : 'wall-to-glass';
    s1.latchType = latchPosition(gc, g1, section.cfG1LatchEnd);
    // One latch per gate, in the order their lines are listed (cf-glass-preview-fixes.js reads it).
    calculation.cfLatchOrder = [s1.latchType].concat(info.filter(function (g) { return g.num !== g1.num; })
      .map(function (g) { return latchPosition(gc, g, section.cfG2LatchEnd); }));
    var res = oQuote.call(this, sectionNum, s1, calculation);
    var breakdown = (res.breakdown || []).slice(), subtotal = res.subtotal || 0;
    /* The calculator matches the latch colour exactly ('black' against 'Matte Black'), so a black or
       white WALL latch falls back to the Mirror one (46095) under a black/white label - found here,
       3 Oct 2026. Gate 1's latch in a custom section is resolved the same way as gate 2's. */
    try {
      var lv = findLatch(gc, s1.latchType, colourKey(gc, section));
      breakdown.forEach(function (l) {
        if (!l || l.category !== 'Gate Hardware' || !/^Gate Latch/.test(l.item || '') || !lv || l.variantId === lv.id) return;
        var np = PriceHelper.fromCents(lv.price);
        subtotal += np * (l.quantity || 1) - (l.totalPrice || 0);
        l.variantId = lv.id; l.unitPrice = np; l.totalPrice = np * (l.quantity || 1); delete l.sku;
      });
    } catch (e) { warn('latch', e); }
    // Gate 2: the same three parts, priced from the same products.
    info.filter(function (g) { return g.num !== g1.num; }).forEach(function (g) {
      var lines = gateLines(gc, section, g, latchPosition(gc, g, section.cfG2LatchEnd));
      lines.forEach(function (l) { breakdown.push(l); subtotal += l.totalPrice; });
    });
    // Stainless steel hinge posts.
    var nPosts = postsOf(calculation);
    if (nPosts) {
      var ck = colourKey(gc, section);
      var fix = postFix(gc, section);
      if (fix === 'inground' && !postFor(ck, 'inground') && postFor(ck, 'base')) fix = 'base';
      var pp = postFor(ck, fix) || postFor('mirror', fix);
      var unit = pp ? pp.cents / 100 : 0;
      var colName = fix === 'base' ? (ck === 'black' ? 'Matte Black' : ck === 'white' ? 'Matte White' : 'Mirror') : (ck === 'black' && postFor('black') ? 'Matte Black' : 'Mirror');
      var line = { category: 'Gate Hardware', item: (fix === 'base' ? 'Stainless Steel Gate Post 50 x 50 x 1300mm, Base Plate, ' : 'Stainless Steel Gate Post 50 x 50 x 1800mm ') + colName,
                   quantity: nPosts, unitPrice: unit, totalPrice: unit * nPosts, variantId: pp && pp.id ? pp.id : null };
      if (pp && pp.sku) line.sku = pp.sku;
      breakdown.push(line);
      subtotal += line.totalPrice;
    }
    var rank = function (it) {
      var c = it.category, n = it.item || '';
      if (c === 'Frameless Glass') return /rake/i.test(n) ? 15 : 10;
      if (c === 'Gate') return 20;
      if (c === 'Hardware') return /spigot/i.test(n) ? 25 : (/clamp/i.test(n) ? 35 : 30);
      if (c === 'Gate Hardware') return /post/i.test(n) ? 32 : 30;
      return 40;
    };
    breakdown.sort(function (a, b) { return rank(a) - rank(b); });
    return { subtotal: subtotal, breakdown: breakdown };
  };

  // The latch variant for a position ('Glass to Glass', 'Wall to Glass', 'Corner ...') in the
  // hardware colour, matched loosely ('black' finds 'Matte Black').
  function findLatch(gc, latchPos, colour) {
    var gl = ((gc.shopifyData && gc.shopifyData.products) || {}).gateLatch;
    if (!gl || !gl.variants) return null;
    var pos = String(latchPos || '').toLowerCase(), col = String(colour || '').toLowerCase();
    return gl.variants.find(function (x) { return x.available && String(x.position || '').toLowerCase() === pos && String(x.color || '').toLowerCase().indexOf(col) >= 0; })
      || gl.variants.find(function (x) { return x.available && String(x.position || '').toLowerCase() === pos; })
      || gl.variants.find(function (x) { return x.available; }) || null;
  }

  function gateLines(gc, section, g, latchPos) {
    var out = [];
    var products = (gc.shopifyData && gc.shopifyData.products) || {};
    var fin = gc.resolveSectionFinish(section);
    var rates = gc._getPriceGuideRates();
    var size = String(g.w);
    var leaf = products.gateLeaf, lv = null;
    if (leaf && leaf.variants) lv = leaf.variants.find(function (v) { return String(v.title).trim() === size; })
      || leaf.variants.find(function (v) { return v.available; }) || leaf.variants[0];
    var lp = lv ? PriceHelper.fromCents(lv.price) : rates.perGate;
    out.push({ category: 'Gate', item: 'Gate Panel ' + size + ' x 1200mm', quantity: 1, unitPrice: lp, totalPrice: lp, variantId: lv ? lv.id : null });
    var hingeType = fin.hingeType || 'spring', hlc = fin.hingeLatchColour;
    var wallMount = g.mount !== 'glass';            // a wall or a post takes the wall/post hinge
    var mountLabel = wallMount ? 'Wall to Glass' : 'Glass to Glass';
    if (hingeType === 'soft-close') {
      var s155 = products.hingeS155;
      var scProduct = (s155 && s155.variants && s155.variants.length) ? s155 : products.hingeSoftClose;
      var scv = gc._matchHardwareVariant(scProduct, hlc, wallMount ? 'wall' : 'glass to glass');
      var scp = scv ? PriceHelper.fromCents(scv.price) : rates.softCloseHinge;
      out.push({ category: 'Gate Hardware', item: 'Polaris S155 Soft Close Hinges (pair) ' + gc._colourDisplayName(hlc) + ' ' + mountLabel,
                 quantity: 1, unitPrice: scp, totalPrice: scp, variantId: scv ? scv.id : null });
    } else {
      var spv = gc._matchHardwareVariant(products.hingeStandard, hlc, mountLabel);
      if (spv) {
        var spp = PriceHelper.fromCents(spv.price);
        out.push({ category: 'Gate Hardware', item: 'Spring Hinge ' + gc._colourDisplayName(hlc) + ' ' + mountLabel,
                   quantity: 1, unitPrice: spp, totalPrice: spp, variantId: spv.id });
      }
    }
    var latchColor = String(hlc || section.latchColor || 'black').toLowerCase();
    var v = findLatch(gc, latchPos, latchColor);
    if (v) {
      var lpr = PriceHelper.fromCents(v.price);
      out.push({ category: 'Gate Hardware', item: 'Gate Latch ' + gc._colourDisplayName(latchColor) + ' ' + (/wall/i.test(latchPos) ? 'Wall to Glass' : (/corner/i.test(latchPos) ? latchPos : 'Glass to Glass')),
                 quantity: 1, unitPrice: lpr, totalPrice: lpr, variantId: v.id });
    }
    return out;
  }

  /* Supply & Install: the installation line counts one gate per section. Each extra gate is
     installed at the same $180 a gate, inside the same small-job loading; each stainless post is
     the post plus $300 to install it (Pete, 3 Oct 2026). Recomputed whole so the rounding matches
     the calculator's own line exactly. */
  var oUQD = P.updateQuoteDisplay;
  P.updateQuoteDisplay = function () {
    var r = oUQD.apply(this, arguments);
    try { adjustInstall(this); } catch (e) { warn('install', e); }
    return r;
  };
  function adjustInstall(gc) {
    if (gc.state.pricingType !== 'install') return;
    var metres = 0, gates = 0, rakes = 0, raw = 0, posts = 0, any = false;
    Object.keys(gc.state.sections || {}).forEach(function (n) {
      var s = gc.state.sections[n];
      var m = (parseFloat(s.length) || 0) / 1000;
      if (m <= 0) return;
      raw += m; metres += Math.ceil(m);
      var c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
      if (s.hasGate) gates += (c && c.cfGates && !c.cfNoFit) ? Math.max(1, gatesOf(c).length) : 1;
      if (c && c.cfGates && !c.cfNoFit) { posts += postsOf(c); if (gatesOf(c).length > 1 || postsOf(c)) any = true; }
      if (s.hasRaked) rakes++;
    });
    if (!any) return;
    var fee = (metres * 180 + gates * 180 + rakes * 180);
    if (raw > 0 && raw < 8) fee *= 1.30;
    fee = Math.round(fee) + posts * 300;
    var bd = gc.state.quoteBreakdown || [];
    var inst = bd.find(function (l) { return l && l.category === 'Professional Installation'; });
    if (!inst) return;
    var delta = fee - (inst.totalPrice || 0);
    if (!delta) return;
    inst.unitPrice = fee; inst.totalPrice = fee;
    var gst = bd.find(function (l) { return l && l.category === 'Tax'; });
    if (gst) { gst.unitPrice += delta * 0.1; gst.totalPrice += delta * 0.1; }
    gc.state.quote = (gc.state.quote || 0) + delta * 1.1;
    try { gc.updateQuoteElements(gc.state.quote); } catch (e) {}
    try { if (gc.shopifyData.settings?.showQuoteBreakdown !== false) gc.updateQuoteBreakdown(); } catch (e) {}
  }

  /* Grout: one bag per 6 core-drilled spigots is the calculator's own recommendation; the stainless
     posts are grouted too - one bag covers two posts (Pete, 3 Oct 2026). Added to the recommended
     bags only while the customer has not typed their own number. */
  var oAddOns = P.populateAddOns;
  if (typeof oAddOns === 'function') {
    P.populateAddOns = function () {
      var r = oAddOns.apply(this, arguments);
      try { addPostGrout(this); } catch (e) { warn('grout', e); }
      return r;
    };
  }
  function addPostGrout(gc) {
    var posts = 0;
    Object.keys(gc.state.sections || {}).forEach(function (n) {
      var c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
      if (isCustom(gc.state.sections[n]) && c && c.cfGates && !c.cfNoFit && postFix(gc, gc.state.sections[n]) !== 'base') posts += postsOf(c);
    });
    if (!posts) return;
    var box = document.getElementById('addon-list');
    if (!box) return;
    var items = box.querySelectorAll('.addon-item');
    Array.prototype.forEach.call(items, function (it) {
      if (!/grout/i.test(it.textContent || '')) return;
      var qty = it.querySelector('input.addon-qty');
      if (!qty || qty.dataset.userSet === '1' || qty.dataset.cfPosts === String(posts)) return;
      var extra = Math.ceil(posts / 2);
      var base = parseInt(qty.dataset.cfBase || qty.value, 10) || 0;
      qty.dataset.cfBase = String(base);
      qty.dataset.cfPosts = String(posts);
      qty.value = String(base + extra);
      var hint = Array.prototype.find.call(it.querySelectorAll('div'), function (d) { return /^Recommended:/.test((d.textContent || '').trim()); });
      if (hint) hint.textContent = hint.textContent.replace(/^Recommended: \d+ bags?/, 'Recommended: ' + (base + extra) + ' bag' + (base + extra !== 1 ? 's' : ''))
        .replace(/\)\s*(—|-)\s*adjust as needed/, ', plus ' + extra + ' for the ' + (posts > 1 ? posts + ' stainless posts' : 'stainless post') + ') — adjust as needed');
    });
    try { gc.updateComponentsQuote(); } catch (e) {}
  }

  /* ---------------------------------------------------------------- what the quote page receives */

  var oProceed = P.proceedToQuotePage;
  P.proceedToQuotePage = function () {
    var out = oProceed.apply(this, arguments);
    try { stampPayload(this); } catch (e) { warn('payload', e); }
    return out;
  };
  function enrichSections(gc, sections) {
    (sections || []).forEach(function (ps) {
      var s = gc.state.sections[ps.num];
      var c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[ps.num];
      if (!s || !isCustom(s) || !c || !c.cfGates || c.cfNoFit) return;
      ps.panels = c.exactPanels.map(function (p) {
        var o = { type: p.type, width: p.width };
        if (p.type === 'gate') { o.flipped = !!p.flipped; o.cfGate = p.cfGate; }
        if (p.cfFacing) o.cfFacing = p.cfFacing;
        if (p.type === 'post') o.cfGate = p.cfGate;
        if (p.type === 'raked') { o.flipped = !!p.flipped; o.retainedHeight = p.retainedHeight; }
        return o;
      });
      ps.startGap = c.exactStartGap;
      ps.trailingGaps = c.exactTrailingGaps.slice();
      ps.hasGate2 = !!s.hasGate2;
      ps.gates = gatesOf(c).map(function (g) { return { num: g.num, width: g.w, hinge: g.side, hingesTo: g.mount, latchAtEnd: !!g.latchAtEnd }; });
      ps.posts = postsOf(c);
    });
  }
  function stampPayload(gc) {
    var any = Object.keys(gc.state.sections || {}).some(function (n) { return isCustom(gc.state.sections[n]); });
    if (!any) return;
    var raw = sessionStorage.getItem('ullc_quote');
    if (raw) { var q = JSON.parse(raw); enrichSections(gc, q.sections); sessionStorage.setItem('ullc_quote', JSON.stringify(q)); }
    var list = JSON.parse(localStorage.getItem('ullc_quotes') || '[]');
    if (list.length) { enrichSections(gc, list[list.length - 1].sections); localStorage.setItem('ullc_quotes', JSON.stringify(list)); }
  }

  /* ---------------------------------------------------------------- Final Preview header */

  var oFinal = P.updateFinalSectionsPreview;
  if (typeof oFinal === 'function') {
    P.updateFinalSectionsPreview = function () {
      var r = oFinal.apply(this, arguments);
      try { fixFinalHeaders(this); } catch (e) {}
      return r;
    };
  }
  function summary(c) {
    var glass = c.exactPanels.filter(function (p) { return p.type === 'panel' || p.type === 'hinge-panel'; }).map(function (p) { return p.width; });
    var groups = {};
    glass.forEach(function (w) { groups[w] = (groups[w] || 0) + 1; });
    var keys = Object.keys(groups);
    var panels = keys.length === 1 ? (glass.length + ' × ' + keys[0] + 'mm') : keys.map(function (w) { return w + 'mm(×' + groups[w] + ')'; }).join(', ');
    var gs = {};
    c.gapSpacings.forEach(function (g) { if (g > 0) gs[g] = (gs[g] || 0) + 1; });
    var gaps = Object.keys(gs).map(function (g) { return g + 'mm(×' + gs[g] + ')'; }).join(', ');
    var gates = gatesOf(c).slice().sort(function (a, b) { return a.num - b.num; }).map(function (g) { return g.w + 'mm'; }).join(' + ');
    var posts = postsOf(c);
    return { panels: panels, gaps: gaps, gates: gates, posts: posts };
  }
  function fixFinalHeaders(gc) {
    var host = document.querySelector('#final-sections-container');
    if (!host) return;
    var wrappers = host.querySelectorAll('.section-preview-wrapper');
    wrappers.forEach(function (w) {
      var lab = w.querySelector('.section-preview-header span');
      var m = lab && /Section\s+(\d+)/.exec(lab.textContent || '');
      if (!m) return;
      var n = parseInt(m[1], 10), s = gc.state.sections[n], c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
      if (!s || !isCustom(s) || !c || !c.cfGates || c.cfNoFit) return;
      var det = w.querySelector('.section-preview-details');
      if (!det) return;
      var sm = summary(c);
      det.textContent = 'Length: ' + s.length + 'mm | Panels: ' + sm.panels + ' | Gap: ' + sm.gaps +
        ' | Gate' + (gatesOf(c).length > 1 ? 's' : '') + ': ' + sm.gates + (sm.posts ? ' | Stainless post' + (sm.posts > 1 ? 's: ' + sm.posts : '') : '') +
        (s.hasRaked ? ' | Raked' : '');
    });
  }

  /* ---------------------------------------------------------------- after each solve */

  function afterCalc(gc, n, s) {
    var c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
    syncUi(gc, n);
    var note = document.getElementById('section-' + n + '-cf2g-note');
    var details = document.getElementById('calc-details');
    if (!c || !c.cfGates) return;
    if (c.cfNoFit) {
      var msg = noFitMessage(c);
      if (note) { note.textContent = msg; note.style.color = '#b45309'; }
      showNoFit(gc, n, msg);
      maybeSnapLength(gc, n, s);
      return;
    }
    var moved = gatesOf(c).filter(function (g) { return g.place === 'measured' && g.shift; });
    if (note) {
      note.style.color = '#6b7280';
      note.textContent = moved.length
        ? moved.map(function (g) { return 'Gate ' + g.num + ' moved ' + Math.abs(g.shift) + 'mm so whole panels fit.'; }).join(' ')
        : '';
    }
    if (details && gc.state.currentSection === n) {
      var sm = summary(c);
      details.innerHTML = '<strong>Section ' + n + ' — Blueprint</strong><br><br>' +
        '<span style="color:#374151;font-weight:600;">Glass panels:</span> ' + sm.panels + '<br>' +
        '<span style="color:#374151;font-weight:600;">Gaps:</span> ' + sm.gaps + '<br>' +
        '<span style="color:#374151;font-weight:600;">Gate' + (gatesOf(c).length > 1 ? 's' : '') + ':</span> ' + sm.gates + '<br>' +
        (sm.posts ? '<span style="color:#374151;font-weight:600;">Stainless steel post' + (sm.posts > 1 ? 's' : '') + ':</span> ' + sm.posts + '<br>' : '') +
        '<br><span style="color:#374151;font-weight:600;">Section Total:</span> <span style="color:#059669;font-weight:600;">' + num(s.length) + 'mm ✓</span><br>';
    }
  }

  function noFitMessage(c) {
    return c.cfError === 'rake-end'
      ? 'The raked panel is at that end, so a gate cannot go there. Choose the other end, or measure the gate from an end.'
      : 'These gates do not fit where they are with whole panels. Move a gate, or make the section longer.';
  }
  function showNoFit(gc, n, msg) {
    var prev = document.getElementById('section-preview');
    if (prev && gc.state.currentSection === n) {
      prev.innerHTML = '<div class="preview-placeholder"><p style="font-size:1rem;color:#b45309;">' + msg + '</p></div>';
    }
  }

  // The same gates, one against each end, hinging to that end - the most compact way to build them.
  function compactCfg(cfg) {
    var c = {}; for (var k in cfg) c[k] = cfg[k];
    c.gates = cfg.gates.map(function (g, i) {
      var o = {}; for (var k2 in g) o[k2] = g[k2];
      var end = i === 0 ? 'left' : 'right';
      if (cfg.rake && cfg.rake.side === end) end = end === 'left' ? 'right' : 'left';
      o.place = end; o.side = end;
      return o;
    });
    if (c.gates.length === 2 && c.gates[0].place === c.gates[1].place) c.gates[1].place = c.gates[1].side = (c.gates[0].place === 'left' ? 'right' : 'left');
    return c;
  }
  function placementHint(cfg) {
    var g = cfg.gates;
    if (g.length === 2 && g[0].place === g[1].place && g[0].place !== 'measured') return 'Both gates are set to the same end. Put one gate at each end, or measure one of them from an end.';
    return 'The gates fit in this section, just not where they are now. Move a gate (or measure it from the other end) and it will fit.';
  }

  /* A length that cannot hold these gates is snapped up to the shortest one that can, with a
     note - the single-gate rule (Pete, 30 Aug 2026). Never while the customer is still typing. */
  function maybeSnapLength(gc, n, s) {
    var input = document.getElementById('section-' + n + '-length');
    if (input && document.activeElement === input) {
      if (!input.__cf2gBlur) {
        input.__cf2gBlur = true;
        input.addEventListener('blur', function () {
          setTimeout(function () {
            var sec = gc.state.sections[n], c2 = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
            if (sec && isCustom(sec) && c2 && c2.cfNoFit) maybeSnapLength(gc, n, sec);
          }, 50);
        });
      }
      return;
    }
    var cfg = cfgFor(gc, s);
    if (cfg.rake && cfg.gates.some(function (g) { return g.place === cfg.rake.side; })) return;
    // Only a section that is too short for these gates in ANY position is lengthened. If the
    // gates would fit at the two ends, it is where they were put that does not work - say so,
    // never stretch the fence (a 4178mm run must not become 8728mm because of a measurement).
    var compactL = CF2G.minLength(compactCfg(cfg), cfg.L + 6000);
    if (!compactL || compactL <= cfg.L) {
      var nt = document.getElementById('section-' + n + '-cf2g-note');
      if (nt) { nt.style.color = '#b45309'; nt.textContent = placementHint(cfg); }
      return;
    }
    var minL = compactL;
    gc.updateSectionDataSilently ? gc.updateSectionDataSilently(n, 'length', String(minL)) : (s.length = String(minL));
    s.length = String(minL);
    if (input) input.value = minL + 'mm';
    if (gc.state.optimizedConfigs) delete gc.state.optimizedConfigs[n];
    gc.updateCalculationResults(n);
    try { gc.updateQuoteDisplay(); } catch (e) {}
    var note = document.getElementById('section-' + n + '-cf2g-note');
    var c2 = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
    if (note) {
      note.style.color = '#b45309';
      note.textContent = 'This is the shortest section that fits these gates: ' + minL + 'mm.' +
        ((c2 && c2.cfNoFit) ? ' ' + placementHint(cfg) : '');
    }
  }

  /* ---------------------------------------------------------------- the controls */

  function el(html) { var d = document.createElement('div'); d.innerHTML = html.trim(); return d.firstChild; }
  var LBL = 'font-size:14px;font-weight:600;color:#374151;display:block;margin-bottom:8px;';
  var SEL = 'font-size:14px;';
  var RADIO = 'width:16px;height:16px;accent-color:#0e9ed5;cursor:pointer;';

  function placeRow(n, g) {
    return '' +
      '<div class="form-group" style="margin-bottom:12px;">' +
        '<label style="' + LBL + '">Gate ' + g + ' position</label>' +
        '<select class="form-control" id="section-' + n + '-g' + g + '-place" style="' + SEL + '">' +
          '<option value="left">At the left end</option>' +
          '<option value="right">At the right end</option>' +
          '<option value="measured">Measured from an end</option>' +
        '</select>' +
        '<div id="section-' + n + '-g' + g + '-measure" style="display:none;gap:6px;margin-top:6px;">' +
          '<select id="section-' + n + '-g' + g + '-from" class="form-control" style="flex:0 0 108px;font-size:13px;padding:8px;">' +
            '<option value="left">From Left</option><option value="right">From Right</option></select>' +
          '<input type="number" id="section-' + n + '-g' + g + '-mm" inputmode="numeric" placeholder="e.g. 2000" style="flex:1;min-width:0;padding:8px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">' +
        '</div>' +
        '<div style="font-size:12px;color:#6b7280;margin-top:5px;" id="section-' + n + '-g' + g + '-measure-hint"></div>' +
      '</div>' +
      '<div class="form-group" style="margin-bottom:12px;display:none;" id="section-' + n + '-g' + g + '-latchend-group">' +
        '<label style="' + LBL + '">Gate ' + g + ' latches to</label>' +
        '<select class="form-control" id="section-' + n + '-g' + g + '-latchend" style="' + SEL + '"></select>' +
      '</div>';
  }

  function buildBlock(gc, n) {
    var lengths = '';
    try { lengths = gc.generateGateLengthOptions('soft-close'); } catch (e) { lengths = '<option value="800">800mm</option><option value="850">850mm</option><option value="900">900mm</option>'; }
    var html = '' +
      '<div id="section-' + n + '-cf2g" class="cf2g" style="margin-top:4px;">' +
        '<div class="form-group" style="margin-bottom:12px;">' +
          '<label style="' + LBL + '" id="section-' + n + '-g1-mount-label">Gate hinges to</label>' +
          '<select class="form-control" id="section-' + n + '-g1-mount" style="' + SEL + '"></select>' +
        '</div>' +
        '<div class="form-group" id="section-' + n + '-postfix-group" style="margin-bottom:12px;display:none;">' +
          '<label style="' + LBL + '">How is the post fixed?</label>' +
          '<select class="form-control" id="section-' + n + '-postfix" style="' + SEL + '">' +
            '<option value="inground">Set into the ground</option><option value="base">Bolted down on a base plate</option></select>' +
        '</div>' +
        '<div class="form-group">' +
          '<label class="toggle-label" style="display:flex;align-items:center;justify-content:space-between;">' +
            '<span>Second gate in this section?</span>' +
            '<div class="toggle-switch"><input type="checkbox" id="section-' + n + '-gate2"><span class="slider"></span></div>' +
          '</label>' +
        '</div>' +
        '<div id="section-' + n + '-g1-card" style="display:none;margin-bottom:8px;">' + placeRow(n, 1) + '</div>' +
        '<div id="section-' + n + '-g2-card" style="display:none;border-top:1px solid #e5e7eb;padding-top:12px;">' +
          '<div class="form-group" style="margin-bottom:12px;">' +
            '<label style="' + LBL + '">Gate 2 Length</label>' +
            '<select class="form-control" id="section-' + n + '-g2-length" style="' + SEL + '">' + lengths + '</select>' +
          '</div>' +
          '<div class="form-group" style="margin-bottom:12px;">' +
            '<label style="' + LBL + '">Gate 2 Hinge Side</label>' +
            '<div style="display:flex;gap:24px;align-items:center;">' +
              '<label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;color:#374151;"><input type="radio" name="section-' + n + '-g2-side" id="section-' + n + '-g2-side-left" value="left" style="' + RADIO + '"> LHS Hinge</label>' +
              '<label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;color:#374151;"><input type="radio" name="section-' + n + '-g2-side" id="section-' + n + '-g2-side-right" value="right" checked style="' + RADIO + '"> RHS Hinge</label>' +
            '</div>' +
          '</div>' +
          placeRow(n, 2) +
          '<div class="form-group" style="margin-bottom:12px;">' +
            '<label style="' + LBL + '">Gate 2 hinges to</label>' +
            '<select class="form-control" id="section-' + n + '-g2-mount" style="' + SEL + '"></select>' +
          '</div>' +
        '</div>' +
        '<div id="section-' + n + '-cf2g-note" style="font-size:12px;color:#6b7280;margin-top:4px;"></div>' +
      '</div>';
    return el(html);
  }

  var MOUNT_TEXT = { wall: 'Wall', glass: 'Glass hinge panel', post: 'Stainless steel post (we supply it)' };

  /* Every option is always in the list (hidden when it does not apply), so a remembered value can be
     set back before this sync has run - cf-calc-memory.js replays controls in document order. */
  function fillMount(sel, atEnd, value, postOk) {
    if (!sel) return;
    if (sel.options.length !== 3) sel.innerHTML = ['wall', 'glass', 'post'].map(function (o) { return '<option value="' + o + '">' + MOUNT_TEXT[o] + '</option>'; }).join('');
    var allowed = { wall: !!atEnd, glass: !atEnd, post: !!postOk };
    Array.prototype.forEach.call(sel.options, function (o) { var ok = allowed[o.value]; o.hidden = !ok; o.disabled = !ok; });
    var want = allowed[value] ? value : (atEnd ? 'wall' : 'glass');
    sel.value = want;
  }

  function fillLatchEnd(gc, sel, value) {
    if (!sel) return;
    var positions = [];
    try {
      ((gc.shopifyData.products.gateLatch || {}).variants || []).forEach(function (v) {
        if (v.available !== false && v.position && !/glass to glass/i.test(v.position) && positions.indexOf(v.position) < 0) positions.push(v.position);
      });
    } catch (e) {}
    if (!positions.length) positions = ['Wall to Glass'];
    var text = function (p) { return /wall/i.test(p) ? 'Wall' : p.replace(/^Corner /i, 'Corner glass panel (') + (/^Corner /i.test(p) ? ')' : ''); };
    var cur = Array.prototype.map.call(sel.options, function (o) { return o.value; }).join('|');
    if (cur !== positions.join('|')) sel.innerHTML = positions.map(function (p) { return '<option value="' + p + '">' + text(p) + '</option>'; }).join('');
    sel.value = positions.indexOf(value) >= 0 ? value : positions[0];
  }

  // The single gate's own placing controls (Move / Middle / Exact) are hidden while the section
  // is in custom mode - the position fields above replace them.
  function singlePlacing(n) {
    var b = document.getElementById('section-' + n + '-gate-move-left');
    return b ? b.closest('.form-group') : null;
  }

  function syncUi(gc, n) {
    var s = gc.state.sections[n];
    var box = document.getElementById('section-' + n + '-cf2g');
    if (!s || !box) return;
    box.style.display = s.hasGate ? '' : 'none';
    if (!s.hasGate) return;
    var custom = isCustom(s);
    var ck = colourKey(gc, s);
    var postOk = anyPost(gc, ck);
    var c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
    var info = (c && c.cfGates && !c.cfNoFit) ? gatesOf(c) : [];
    var q = function (id) { return document.getElementById('section-' + n + '-' + id); };

    // gate 1 mount: in automatic mode it mirrors what the calculator worked out
    var g1AtEnd;
    if (custom) g1AtEnd = CF2G.hingeAtEnd({ place: s.cfG1Place || 'left', side: s.gateSide === 'left' ? 'left' : 'right' });
    else g1AtEnd = (s.gateHingeType === 'wall-to-glass');
    var g1Val = s.cfG1Mount === 'post' ? 'post' : (custom ? (s.cfG1Mount || (g1AtEnd ? 'wall' : 'glass')) : (g1AtEnd ? 'wall' : 'glass'));
    fillMount(q('g1-mount'), g1AtEnd, g1Val, postOk || s.cfG1Mount === 'post');
    var lab = q('g1-mount-label');
    if (lab) lab.textContent = s.hasGate2 ? 'Gate 1 hinges to' : 'Gate hinges to';
    // With one gate the first card's labels say "Gate", not "Gate 1".
    Array.prototype.forEach.call(box.querySelectorAll('#section-' + n + '-g1-card label'), function (l) {
      l.textContent = l.textContent.replace(/^Gate( 1)? /, s.hasGate2 ? 'Gate 1 ' : 'Gate ');
    });
    var t2 = q('gate2'); if (t2) t2.checked = !!s.hasGate2;

    q('g1-card').style.display = custom ? '' : 'none';
    q('g2-card').style.display = s.hasGate2 ? '' : 'none';
    var sp = singlePlacing(n);
    if (sp) sp.style.display = custom ? 'none' : '';

    [1, 2].forEach(function (g) {
      if (g === 2 && !s.hasGate2) return;
      if (g === 1 && !custom) return;
      var pre = 'cfG' + g;
      var place = s[pre + 'Place'] || (g === 1 ? 'left' : 'right');
      var side = g === 1 ? (s.gateSide === 'left' ? 'left' : 'right') : (s.cfG2Side === 'left' ? 'left' : 'right');
      var pl = q('g' + g + '-place'); if (pl) pl.value = place;
      var ms = q('g' + g + '-measure'); if (ms) ms.style.display = place === 'measured' ? 'flex' : 'none';
      var fr = q('g' + g + '-from'); if (fr) fr.value = s[pre + 'From'] || (g === 1 ? 'left' : 'right');
      var mm = q('g' + g + '-mm'); if (mm && document.activeElement !== mm) mm.value = s[pre + 'Mm'] != null && s[pre + 'Mm'] !== '' ? s[pre + 'Mm'] : '';
      var hint = q('g' + g + '-measure-hint');
      if (hint) hint.textContent = place === 'measured' ? 'Distance from the chosen end to the near edge of the gate.' : '';
      var latchAtEnd = CF2G.latchAtEnd({ place: place, side: side });
      var lg = q('g' + g + '-latchend-group');
      if (lg) lg.style.display = latchAtEnd ? '' : 'none';
      if (latchAtEnd) fillLatchEnd(gc, q('g' + g + '-latchend'), s[pre + 'LatchEnd']);
      if (g === 2) {
        var atEnd = CF2G.hingeAtEnd({ place: place, side: side });
        fillMount(q('g2-mount'), atEnd, s.cfG2Mount || (atEnd ? 'wall' : 'glass'), postOk || s.cfG2Mount === 'post');
        var len = q('g2-length'); if (len) len.value = String(num(s.cfG2Length) || num(s.gateLength) || 800);
        var rl = q('g2-side-left'), rr = q('g2-side-right');
        if (rl) rl.checked = side === 'left'; if (rr) rr.checked = side === 'right';
      }
    });
    var pg = q('postfix-group'), ps = q('postfix');
    if (pg && ps) {
      var hasPost = s.cfG1Mount === 'post' || (s.hasGate2 && s.cfG2Mount === 'post');
      pg.style.display = hasPost ? '' : 'none';
      var inOk = !!postFor(ck, 'inground'), baseOk = !!postFor(ck, 'base');
      ps.options[0].hidden = ps.options[0].disabled = !inOk;
      ps.options[1].hidden = ps.options[1].disabled = !baseOk;
      var f = postFix(gc, s); if (f === 'inground' && !inOk) f = 'base'; if (f === 'base' && !baseOk) f = 'inground';
      ps.value = f;
    }
  }

  function refreshAllUi() {
    var gc = inst();
    if (!gc || !gc.state || !gc.state.sections) return;
    Object.keys(gc.state.sections).forEach(function (n) { try { syncUi(gc, parseInt(n, 10)); } catch (e) {} });
  }

  // Where the single gate is drawn right now, so switching to custom mode does not move it.
  function currentPlacement(gc, n, s) {
    var c = gc.state.optimizedConfigs && gc.state.optimizedConfigs[n];
    var L = num(s.length), w = num(s.gateLength) || 800;
    try {
      if (!c) { gc.updateCalculationResults(n); c = gc.state.optimizedConfigs[n]; }
      var panels = (c && c.gateExact && c.exactPanels) ? c.exactPanels : gc.calculatePanelsLayout({
        calculation: c, sectionLength: L, hasRaked: !!s.hasRaked, hasGate: true, gateWidth: w,
        rakedSide: s.rakedSide || 'left', retainedHeight: s.retainedHeight || DEF_RETAINED,
        hingeType: s.hingeType || 'soft-close', gateFlipped: !!s.gateFlipped, gateSide: s.gateSide || 'right',
        wallGateEnd: s.wallGateEnd || null, gateHingeType: s.gateHingeType || 'glass-to-glass',
        gatePosition: s.gatePosition !== undefined ? s.gatePosition : 0 });
      var start = (c && c.gateExact) ? c.exactStartGap : gc.buildStartGap(c, panels);
      var trail = (c && c.gateExact) ? c.exactTrailingGaps : gc.buildTrailingGaps(panels, c);
      var x = start || 0, gi = -1;
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].type === 'gate') { gi = i; break; }
        x += (panels[i].width || 0) + (trail[i] || 0);
      }
      if (gi === 0) return { place: 'left' };
      if (gi === panels.length - 1) return { place: 'right' };
      if (gi > 0) return x < L / 2 ? { place: 'measured', from: 'left', mm: x } : { place: 'measured', from: 'right', mm: Math.max(0, L - x - w) };
    } catch (e) { warn('placement', e); }
    return { place: 'left' };
  }

  function enterCustom(gc, n, s) {
    if (isCustom(s)) return;
    var p = currentPlacement(gc, n, s);
    s.cfG1Place = p.place;
    if (p.place === 'measured') { s.cfG1From = p.from; s.cfG1Mm = Math.round(p.mm); }
  }

  function recalc(gc, n) {
    if (gc.state.optimizedConfigs) delete gc.state.optimizedConfigs[n];
    try { gc.updateCalculationResults(n); } catch (e) { warn('recalc', e); }
    try { gc.renderSectionPreview(n); } catch (e) {}
    try { gc.trigger3DUpdate(); } catch (e) {}
    try { gc.updateQuoteDisplay(); } catch (e) {}
    try { gc.updateComponentsQuote(); } catch (e) {}
    try { syncUi(gc, n); } catch (e) {}
  }

  function wire(gc, n, box) {
    var q = function (id) { return box.querySelector('#section-' + n + '-' + id); };
    var S = function () { return gc.state.sections[n]; };

    q('g1-mount').addEventListener('change', function (e) {
      var s = S(); if (!s) return;
      var v = e.target.value;
      if (!v) return;
      if (v === 'post') { enterCustom(gc, n, s); s.cfG1Mount = 'post'; }
      else if (isCustom(s)) { s.cfG1Mount = v; }            // an explicit choice stays explicit
      else { delete s.cfG1Mount; }                          // same as the calculator's own: stay automatic
      recalc(gc, n);
    });

    q('gate2').addEventListener('change', function (e) {
      var s = S(); if (!s) return;
      if (e.target.checked) {
        enterCustom(gc, n, s);
        var g1Left = s.cfG1Place === 'left' || (s.cfG1Place === 'measured' && (s.cfG1From || 'left') === 'left');
        s.hasGate2 = true;
        if (!s.cfG2Place) {
          s.cfG2Place = g1Left ? 'right' : 'left';
          s.cfG2Side = g1Left ? 'right' : 'left';
          s.cfG2Length = s.cfG2Length || String(num(s.gateLength) || 800);
          s.cfG2From = s.cfG2Place;
        }
        var test = CF2G.solve(cfgFor(gc, s));
        if (test.error) {
          // Defaults did not fit - try one gate at each end before giving up.
          var cc = compactCfg(cfgFor(gc, s));
          if (!CF2G.solve(cc).error) {
            s.cfG1Place = cc.gates[0].place; s.gateSide = cc.gates[0].side;
            s.cfG2Place = cc.gates[1].place; s.cfG2Side = cc.gates[1].side;
            test = { ok: true };
          }
        }
        if (test.error) {
          var minL = CF2G.minLength(compactCfg(cfgFor(gc, s)), num(s.length) + 6000);
          s.hasGate2 = false;
          e.target.checked = false;
          try {
            gc.showValidationPopup('Two gates need a longer section' + (minL ? ' - at least ' + minL + 'mm with these gates.' : '.') +
              '\n\nMake this section longer, or send us a drawing on the "Send us your plans" page and we will advise.');
          } catch (x) {}
          if (s.cfG1Mount !== 'post') delete s.cfG1Mount;
        }
      } else {
        s.hasGate2 = false;
        if (s.cfG1Mount !== 'post') delete s.cfG1Mount;
      }
      recalc(gc, n);
    });

    [1, 2].forEach(function (g) {
      var pre = 'cfG' + g;
      q('g' + g + '-place').addEventListener('change', function (e) {
        var s = S(); if (!s) return;
        s[pre + 'Place'] = e.target.value;
        if (e.target.value === 'measured' && (s[pre + 'Mm'] == null || s[pre + 'Mm'] === '')) {
          s[pre + 'From'] = s[pre + 'From'] || 'left';
          s[pre + 'Mm'] = Math.round((num(s.length) - (num(g === 1 ? s.gateLength : s.cfG2Length) || 800)) / 2);
        }
        recalc(gc, n);
      });
      q('g' + g + '-from').addEventListener('change', function (e) { var s = S(); if (!s) return; s[pre + 'From'] = e.target.value; recalc(gc, n); });
      var mm = q('g' + g + '-mm');
      var commit = function () { var s = S(); if (!s) return; var v = num(mm.value); if (String(s[pre + 'Mm']) === String(v)) return; s[pre + 'Mm'] = v; recalc(gc, n); };
      mm.addEventListener('change', commit);
      mm.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); commit(); } });
      q('g' + g + '-latchend').addEventListener('change', function (e) { var s = S(); if (!s) return; s[pre + 'LatchEnd'] = e.target.value; recalc(gc, n); });
    });
    q('g2-length').addEventListener('change', function (e) { var s = S(); if (!s) return; s.cfG2Length = e.target.value; recalc(gc, n); });
    if (q('postfix')) q('postfix').addEventListener('change', function (e) { var s = S(); if (!s) return; s.cfPostFix = e.target.value; recalc(gc, n); });
    q('g2-mount').addEventListener('change', function (e) { var s = S(); if (!s || !e.target.value) return; s.cfG2Mount = e.target.value; recalc(gc, n); });
    ['left', 'right'].forEach(function (side) {
      q('g2-side-' + side).addEventListener('change', function (e) { var s = S(); if (!s || !e.target.checked) return; s.cfG2Side = side; recalc(gc, n); });
    });
  }

  var oGateForm = P.createGateConfigForm;
  P.createGateConfigForm = function (sectionNum) {
    var form = oGateForm.apply(this, arguments);
    try {
      var box = buildBlock(this, sectionNum);
      form.appendChild(box);
      wire(this, sectionNum, box);
      var gc = this;
      setTimeout(function () { try { syncUi(gc, sectionNum); } catch (e) {} }, 0);
    } catch (e) { warn('controls', e); }
    return form;
  };

  // Turning the gate off ends custom mode for that section.
  var oToggle = P.toggleGate;
  P.toggleGate = function (sectionNum, checked) {
    var s = this.state && this.state.sections && this.state.sections[sectionNum];
    if (!checked && s) { s.hasGate2 = false; delete s.cfG1Mount; }
    var r = oToggle.apply(this, arguments);
    var gc = this;
    setTimeout(function () { try { syncUi(gc, sectionNum); } catch (e) {} }, 0);
    return r;
  };

  // Any other recalculation path (colour, hinge type, panel size) keeps the controls current.
  var oRSP = P.renderSectionPreview;
  P.renderSectionPreview = function (sectionNum) {
    var r = oRSP.apply(this, arguments);
    try {
      var s = this.state.sections[sectionNum];
      if (s && s.hasGate) syncUi(this, sectionNum);
      var c = this.state.optimizedConfigs && this.state.optimizedConfigs[sectionNum];
      if (s && isCustom(s) && c && c.cfNoFit) showNoFit(this, sectionNum, noFitMessage(c));
    } catch (e) {}
    return r;
  };

  P.__cfTwoGatesPatched = true;
  window.CF2G = CF2G;
  loadPostProduct();
  try { console.log(TAG + ' installed (two gates per section, stainless post hinge)'); } catch (e) {}
})();
